import { useState } from "react";
import { Minus, Plus, Loader2, ShoppingBag } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useCartStore, createShopifyProductFromLocal } from "@/stores/cartStore";
import type { Product } from "@/data/products";

interface QuickAddButtonProps {
  product: Product;
  /** Show full-width style (default true) */
  fullWidth?: boolean;
}

const QuickAddButton = ({ product, fullWidth = true }: QuickAddButtonProps) => {
  const items = useCartStore((s) => s.items);
  const addItem = useCartStore((s) => s.addItem);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const isLoading = useCartStore((s) => s.isLoading);
  const [isAdding, setIsAdding] = useState(false);

  const cartItem = items.find((i) => i.variantId === product.shopifyVariantId);
  const quantity = cartItem?.quantity ?? 0;

  const handleAdd = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAdding(true);
    try {
      const shopifyData = createShopifyProductFromLocal({
        name: product.name,
        tagline: product.tagline,
        price: product.price,
        image: product.images[0],
        slug: product.slug,
        shopifyVariantId: product.shopifyVariantId,
      });
      await addItem({ ...shopifyData, quantity: 1 });

      // Meta Pixel AddToCart
      if ((window as any).fbq) {
        (window as any).fbq("track", "AddToCart", {
          content_name: product.name,
          content_ids: [product.shopifyVariantId],
          value: product.price,
          currency: "MXN",
        });
      }

      // GA4 add_to_cart
      if ((window as any).gtag) {
        (window as any).gtag("event", "add_to_cart", {
          currency: "MXN",
          value: product.price,
          items: [{ item_id: product.shopifyVariantId, item_name: product.name, price: product.price, quantity: 1 }],
        });
      }

      toast.success(`✓ ${product.name} agregado al carrito`, { position: "top-right", duration: 3000 });
    } finally {
      setIsAdding(false);
    }
  };

  const handleIncrement = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await updateQuantity(product.shopifyVariantId, quantity + 1);
  };

  const handleDecrement = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await updateQuantity(product.shopifyVariantId, quantity - 1);
  };

  return (
    <div className={fullWidth ? "w-full" : ""} onClick={(e) => e.stopPropagation()}>
      <AnimatePresence mode="wait" initial={false}>
        {quantity === 0 ? (
          <motion.div
            key="add"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <Button
              className={`${fullWidth ? "w-full" : ""} min-h-[44px]`}
              onClick={handleAdd}
              disabled={isLoading || isAdding}
            >
              {isAdding ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
              ) : (
                <ShoppingBag className="w-4 h-4 mr-1.5" />
              )}
              Agregar — ${product.price}
            </Button>
          </motion.div>
        ) : (
          <motion.div
            key="qty"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className={`flex items-center ${fullWidth ? "w-full" : ""}`}
          >
            <div className="flex items-center w-full bg-primary rounded-md overflow-hidden min-h-[44px]">
              <button
                onClick={handleDecrement}
                disabled={isLoading}
                className="flex items-center justify-center w-12 h-[44px] text-primary-foreground hover:bg-primary/80 transition-colors disabled:opacity-40"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="flex-1 text-center text-sm font-semibold text-primary-foreground tabular-nums">
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : quantity}
              </span>
              <button
                onClick={handleIncrement}
                disabled={isLoading}
                className="flex items-center justify-center w-12 h-[44px] text-primary-foreground hover:bg-primary/80 transition-colors disabled:opacity-40"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default QuickAddButton;
